import React from 'react';
import { BsX, BsFileEarmarkPdf, BsPersonPlus, BsTrash, BsCurrencyDollar } from 'react-icons/bs';

interface HelpDrawerCronogramaProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string; borderColor?: string; textColor?: string }> = ({ icon, label, description, bgColor, borderColor, textColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: `1px solid ${borderColor || '#ccc'}`,
      backgroundColor: bgColor,
      color: textColor || 'black',
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerCronograma: React.FC<HelpDrawerCronogramaProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Cronograma PDF</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: 0 }}>
              Gera um documento PDF de cronograma para ofício. Preencha as seções e clique em "Gerar PDF" para baixar.
            </p>
          </Section>

          <Section title="Seções do Formulário">
            <ActionItem icon={<span style={{ fontSize: '10px' }}>📋</span>} label="Configurações Básicas" description="Selecione o processo. O cronograma de atividades é carregado automaticamente do sistema." bgColor="#0d6efd" borderColor="#0d6efd" textColor="white" />
            <ActionItem icon={<span style={{ fontSize: '10px' }}>#</span>} label="Número do Ofício" description="Informe o número do ofício (ex: 001/2024)." bgColor="#6c757d" borderColor="#6c757d" textColor="white" />
            <ActionItem icon={<BsPersonPlus style={{ fontSize: '10px' }} />} label="Destinatários" description="Adicione os destinatários do ofício com tratamento, nome, cargo e unidade." bgColor="#0dcaf0" borderColor="#0dcaf0" textColor="white" />
            <ActionItem icon={<span style={{ fontSize: '10px' }}>📊</span>} label="Estimativa de Bancas" description="Informe tipo (Facial, Virtual, Recursal), quantidade e observações para cada banca prevista." bgColor="#ffc107" borderColor="#ffc107" />
            <ActionItem icon={<BsCurrencyDollar style={{ fontSize: '10px' }} />} label="Estimativa de Custo" description="Tabela com valor por pessoa e quantidade para cada categoria de membro, por tipo de banca." bgColor="#198754" borderColor="#198754" textColor="white" />
            <ActionItem icon={<span style={{ fontSize: '10px' }}>✍</span>} label="Assinaturas" description="Nome e cargo da Coordenadora e do Presidente da Comissão." bgColor="#212529" borderColor="#212529" textColor="white" />
          </Section>

          <Section title="Estimativa de Custo">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Valor Global:</strong> Aplica o mesmo valor por pessoa em todas as categorias e tipos de banca.</li>
              <li><strong>Resetar Tabela:</strong> Restaura os valores padrão.</li>
              <li>Cada célula é editável individualmente.</li>
              <li>Colunas coloridas: <span style={{ color: '#0d6efd' }}>Facial</span>, <span style={{ color: '#20c997' }}>Virtual</span>, <span style={{ color: '#fd7e14' }}>Recursal</span>.</li>
            </ul>
          </Section>

          <Section title="Ações">
            <ActionItem icon={<BsFileEarmarkPdf />} label="Gerar PDF" description="Gera e baixa o documento PDF com todas as informações preenchidas." bgColor="#0d6efd" borderColor="#0d6efd" textColor="white" />
            <ActionItem icon={<BsPersonPlus />} label="Adicionar" description="Adiciona novo destinatário ou nova estimativa de banca." bgColor="white" borderColor="#0d6efd" textColor="#0d6efd" />
            <ActionItem icon={<BsTrash />} label="Remover" description="Remove um destinatário, banca ou categoria de custo." bgColor="white" borderColor="#dc3545" textColor="#dc3545" />
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>O cronograma é carregado das atividades cadastradas no processo.</li>
              <li>Selecione o processo primeiro — os dados são carregados automaticamente.</li>
              <li>Todos os campos são opcionais exceto o processo.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerCronograma;
